import { useCallback, useContext, useMemo } from "react";

import { ArrowSquareOut, CopySimple } from "@magnetic/icons";
import { Text } from "@magnetic/text";
import { Tooltip } from "@magnetic/tooltip";

import { Button } from "src/components/buttons";
import { HFlex } from "src/components/hFlex";
import { DataProviderContext } from "src/core/provider/dataProvider";
import { TimeZoneContext } from "src/core/timeZoneProvider";
import { FORMAT } from "src/dataTypes/dateFormats";

import { buildMockShellPopOutUrl } from "./mockShellUtils";

import "./mockShellItemHeader.scss";

interface MockShellItemHeaderProps {
  readonly command: string;
  readonly executedAt: Date | undefined;
  readonly onCopy?: () => void;
  readonly showCopyButton?: boolean;
  readonly showPopOutButton?: boolean;
  readonly selectedDeviceId?: string;
}

export const MockShellItemHeader = ({
  command,
  executedAt,
  onCopy,
  showCopyButton = true,
  showPopOutButton = true,
  selectedDeviceId
}: MockShellItemHeaderProps) => {
  const { fabric } = useContext(DataProviderContext);
  const { getFormattedDate } = useContext(TimeZoneContext);

  const popOutUrl = useMemo(() => {
    if (!fabric?.id || !command) return "";
    return buildMockShellPopOutUrl(fabric.id, command, selectedDeviceId);
  }, [fabric?.id, command, selectedDeviceId]);

  const handlePopOut = useCallback(() => {
    if (popOutUrl) {
      window.open(popOutUrl, "_blank");
    }
  }, [popOutUrl]);

  // Update the prompt text:
  const selectedDevice = useMemo(() => {
    if (!fabric || !selectedDeviceId) {
      return undefined;
    }

    return fabric.nodes.find(
      (node) =>
        node.id === selectedDeviceId || node.deviceId === selectedDeviceId
    );
  }, [fabric, selectedDeviceId]);

  const promptPrefix = useMemo(() => {
    if (!fabric?.name) {
      return "";
    }

    if (!selectedDeviceId) {
      return `${fabric.name}#`;
    }

    return `${fabric.name} ${selectedDevice?.name ?? selectedDeviceId}#`;
  }, [fabric?.name, selectedDevice?.name, selectedDeviceId]);

  return (
    <HFlex
      justify="space-between"
      align="center"
      className="mock-shell-history-item-header"
    >
      <HFlex gap="sm" align="center">
        <Text size="p5" className="mock-shell-history-subcommand">
          {promptPrefix}
        </Text>
        <Text weight="semi-bold" className="mock-shell-history-command">
          {command}
        </Text>
      </HFlex>
      <HFlex gap="sm" align="center">
        <Text size="p5" className="mock-shell-history-subcommand">
          Executed at {getFormattedDate(executedAt, FORMAT.LONG)}
        </Text>
        {showCopyButton && onCopy && (
          <Tooltip title="Copy output">
            <Button
              name="copy-output"
              kind="tertiary"
              size="sm"
              data-qa="mock-shell-copy-output"
              icon={<CopySimple />}
              onClick={onCopy}
            >
              Copy output
            </Button>
          </Tooltip>
        )}
        {showPopOutButton && (
          <Tooltip title="Open command in new window">
            <Button
              name="pop-out-command"
              kind="tertiary"
              size="sm"
              icon={<ArrowSquareOut />}
              data-qa="mock-shell-pop-out-command"
              onClick={handlePopOut}
            />
          </Tooltip>
        )}
      </HFlex>
    </HFlex>
  );
};
